import { removeStoredMutationsById } from '../graphql/util/removeStoredMutationsById'
import type { CacheProxy, Company } from '@paintscout/api'
import {
  updateCachedCompany,
  updateCachedCompanyPartially,
  useDeleteCompaniesMutation,
  useSaveCompanyMutation
} from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import { useMemo } from 'react'
import { useApolloClient } from '@apollo/react-hooks'

/**
 * Returns common useMutations for saving companies and handles updating the caches
 */
export function useCompanyMutations() {
  const [saveCompanyMutation] = useSaveCompanyMutation()
  const [deleteCompaniesMutation] = useDeleteCompaniesMutation()
  const client = useApolloClient()

  return useMemo(
    () => ({
      async saveCompany({ company }: { company: Company }) {
        const timeOfSave = Date.now()
        const mutationId = `save-company-${company._id}`
        const result = await saveCompanyMutation({
          variables: {
            company
          },
          context: {
            __mutation_id__: mutationId
          },

          optimisticResponse: {
            saveCompany: {
              __typename: 'Company' as const,
              ...company,
              owner: company.owner
                ? {
                    __typename: 'Owner',
                    ...company.owner
                  }
                : null,
              _rev: company._rev // null is important, if its undefined it will break cache for quote (undefined is not valid graphql value)
            }
          },
          update(cache: CacheProxy, { data }) {
            if (data?.saveCompany) {
              updateCachedCompany(cache, data.saveCompany)
            }
          }
        })

        removeStoredMutationsById(client, mutationId, { before: timeOfSave })

        return {
          company: result.data?.saveCompany
        }
      },

      async deleteCompanies(ids: string[], skipRefetchQueries = false) {
        return deleteCompaniesMutation({
          variables: { ids },
          optimisticResponse: {
            deleteCompanies: ids.map((id) => ({
              __typename: 'BulkDocsResponseObject',
              _id: id,
              _rev: null,
              error: null
            }))
          },
          update(cache: ApolloCache, { data: _data }) {
            ids.forEach((id) => {
              // we can fake the company document as just an object with _deleted flag
              updateCachedCompanyPartially(cache, id, {
                _deleted: true
              })
            })
          },
          refetchQueries: skipRefetchQueries ? [] : ['searchCompanies']
        })
      }
    }),
    []
  )
}
